import styled from '@emotion/styled';
import {
  Button,
  ButtonProps,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';
import { truncate } from 'lodash';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useDialogState } from '../../hooks/useDialogState';
import { FileType, FILE_TYPE_OPTIONS } from '../../services/file';
import { ButtonWithPromise } from '../ButtonWithPromise';
import {
  exportRowsToFile,
  IExportColumn,
  IExportGroup,
  renderHeads,
  renderRows
} from './service';

type ColumnSelectorProps<ColumnKey, RowData, OtherProps = undefined> = {
  value: ColumnKey[];
  onChange: (nextValue: ColumnKey[]) => void;
  columns: IExportColumn<ColumnKey, RowData, OtherProps>[];
  otherProps: OtherProps;
  groups?: IExportGroup<ColumnKey>;
};

const ColumnSelectorGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: ${(p) => p.theme.spacing(3)}px;
  grid-row-gap: 0;
`;

export const ColumnSelector = <ColumnKey, RowData, OtherProps = undefined>({
  value,
  onChange,
  columns,
  otherProps,
  groups
}: ColumnSelectorProps<ColumnKey, RowData, OtherProps>) => {
  const set = useMemo(() => new Set(value), [value]);
  const add = (k: ColumnKey) => onChange([...value, k]);
  const remove = (k: ColumnKey) => onChange(value.filter((x) => x !== k));

  return (
    <ColumnSelectorGrid>
      {columns.map((c) => {
        const ui = c.ui ? c.ui(otherProps) : null;
        return (
          <FormControlLabel
            key={`${c.key}`}
            label={ui?.label || c.head(otherProps)}
            control={
              <Checkbox
                size="small"
                checked={set.has(c.key)}
                onChange={(ev) =>
                  ev.target.checked ? add(c.key) : remove(c.key)
                }
              />
            }
          />
        );
      })}
    </ColumnSelectorGrid>
  );
};

type ExportPreviewProps<ColumnKey, RowData, OtherProps = undefined> = {
  rows: RowData[];
  otherProps: OtherProps;
  columns: IExportColumn<ColumnKey, RowData, OtherProps>[];
  maxShown?: number;
};

const PreviewTableContainer = styled('div')`
  max-width: 600px;
  overflow: auto;
  font-size: ${(p) => p.theme.custom.fontSize.s}px;

  margin-bottom: ${(p) => p.theme.spacing(3)}px;

  table,
  th,
  td {
    border: ${(p) => p.theme.custom.border.standard};
    border-collapse: collapse;
  }

  th,
  td {
    padding: ${(p) => p.theme.spacing()}px;
    white-space: nowrap;
  }
`;

export const ExportPreview = <ColumnKey, RowData, OtherProps = undefined>({
  rows,
  otherProps,
  columns,
  maxShown = 5
}: ExportPreviewProps<ColumnKey, RowData, OtherProps>) => {
  const heads = renderHeads(otherProps, columns);
  const rs = renderRows(rows.slice(0, maxShown), otherProps, columns);
  return (
    <PreviewTableContainer>
      <table>
        <thead>
          <tr>
            {heads.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rs.map((r, i) => (
            <tr key={i}>
              {r.map((c, ii) => (
                <td key={ii}>{truncate(c, { length: 60, omission: '…' })}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </PreviewTableContainer>
  );
};

export const ExportFormatSelector = ({
  value,
  onChange,
  label
}: {
  value: FileType;
  onChange: (nextValue: FileType) => void;
  label?: React.ReactNode;
}) => {
  const opts = FILE_TYPE_OPTIONS.filter((f) => f.value !== 'pdf');
  return (
    <FormControl variant="outlined" fullWidth={true}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        fullWidth={true}
        value={opts.findIndex((o) => o.value === value)}
        onChange={(ev) => onChange(opts[Number(ev.target.value)].value)}
      >
        {opts.map((o, i) => (
          <MenuItem key={i} value={i}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const TitleRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${(p) => (p.onClick ? 'cursor: pointer;' : '')}
`;

export const ExportDialogInner = <ColumnKey, RowData, OtherProps = undefined>({
  onClose,
  title,
  fileName,
  rows,
  otherProps,
  columns,
  groups,
  maxPreviewed,
  preselectedColumns
}: {
  onClose: () => void;
  title: React.ReactNode;
  fileName: string;
  rows: RowData[];
  otherProps: OtherProps;
  columns: IExportColumn<ColumnKey, RowData, OtherProps>[];
  groups?: IExportGroup<ColumnKey>;
  maxPreviewed?: number;
  preselectedColumns?: ColumnKey[];
}) => {
  const [format, setFormat] = useState(FILE_TYPE_OPTIONS[0].value);
  const [fN, setFn] = useState(fileName);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<ColumnKey[]>(
    preselectedColumns || columns.map((c) => c.key)
  );
  const renderedColumns = useMemo(() => {
    const set = new Set(selectedColumns);
    return columns.filter((c) => set.has(c.key));
  }, [columns, selectedColumns]);

  const columnTitle =
    renderedColumns.length === columns.length
      ? 'All columns'
      : `${renderedColumns.length} of ${columns.length} columns`;

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <h4>File Name</h4>
        <TextField
          fullWidth
          variant="outlined"
          value={fN}
          onChange={(ev) => setFn(ev.target.value)}
          required
        />
        <h4>Preview (First 5 rows)</h4>
        <ExportPreview
          rows={rows}
          otherProps={otherProps}
          columns={renderedColumns}
          maxShown={maxPreviewed}
        />
        <TitleRow onClick={() => setShowColumnSelector((x) => !x)}>
          <h4>{columnTitle}</h4>
          {showColumnSelector ? (
            <ChevronUp size={18} />
          ) : (
            <ChevronDown size={18} />
          )}
        </TitleRow>
        {showColumnSelector && (
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            otherProps={otherProps}
            groups={groups}
          />
        )}
        <h4>Format</h4>
        <ExportFormatSelector value={format} onChange={setFormat} />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          Cancel
        </Button>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          disabled={!fN}
          onClick={() =>
            exportRowsToFile(fN, rows, otherProps, renderedColumns, format)
          }
          pending="Exporting..."
        >
          Export
        </ButtonWithPromise>
      </DialogActions>
    </>
  );
};

export const ExportDialog = <ColumnKey, RowData, OtherProps = undefined>({
  open,
  onClose,
  ...other
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  fileName: string;
  rows: RowData[];
  otherProps: OtherProps;
  columns: IExportColumn<ColumnKey, RowData, OtherProps>[];
  groups?: IExportGroup<ColumnKey>;
  preselectedColumns?: ColumnKey[];
  maxPreviewed?: number;
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <ExportDialogInner {...other} onClose={onClose} />
    </Dialog>
  );
};

export const ExportButton = <ColumnKey, RowData, OtherProps = undefined>({
  dialogTitle,
  fileName,
  rows,
  otherProps,
  columns,
  groups,
  maxPreviewed,
  preselectedColumns,
  ...buttonProps
}: {
  dialogTitle: React.ReactNode;
  fileName: string;
  rows: RowData[];
  otherProps: OtherProps;
  columns: IExportColumn<ColumnKey, RowData, OtherProps>[];
  groups?: IExportGroup<ColumnKey>;
  maxPreviewed?: number;
  preselectedColumns?: ColumnKey[];
} & ButtonProps) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState();
  return (
    <>
      <Button {...buttonProps} onClick={openDialog} />
      <ExportDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={dialogTitle}
        fileName={fileName}
        rows={rows}
        otherProps={otherProps}
        columns={columns}
        groups={groups}
        maxPreviewed={maxPreviewed}
        preselectedColumns={preselectedColumns}
      />
    </>
  );
};
